import React, { FC, ReactNode, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Progress, Tooltip } from 'antd';
import {
    CaretRightOutlined,
    DatabaseOutlined,
    ForwardFilled,
    InfoCircleOutlined,
    MessageOutlined,
    PhoneOutlined,
} from '@ant-design/icons';
import { CardLayout, Catcher, InfiniteScroll, LinkComponent, cardLayoutOptions } from '../../';
import cx from 'classnames';
import dayjs from 'dayjs';
import { appConfig } from '../../../settings';
import { IGetApiType, IPackageListPaginated, ITariffPackageInstance } from '../../../interfaces';
import { useUiIsSpinnerFound } from '../../../hooks';
import { UnitTypeValues, getTariffPackageInstancesList } from '../../../middleware';
import { scrollMainContent, showOriginalUnitByType } from '../../../instruments';
import { tF, tI, tNM } from '../../../translate';
import Styles from './m_packageUsage.less';

interface IPackageUsageStatisticsProps {
    item: ITariffPackageInstance;
}

interface IPackageUsageProps {
    cardId: string;
}
const getCurrentTextLabel = ({ item }: IPackageUsageStatisticsProps): string =>
    `${tI('from')} ${item.startDate ? dayjs(item.startDate).format(appConfig.formatDate) : ''}  ${tI('to')} ${
        item.endDate ? dayjs(item.endDate).format(appConfig.formatDate) : ''
    }`;

const ListLabel: FC<IPackageUsageStatisticsProps> = ({ item }: IPackageUsageStatisticsProps) => {
    return (
        <div className={Styles.label}>
            <p>
                {tI('from')}: <span>{item.startDate ? dayjs(item.startDate).format(appConfig.formatDate) : ''}</span>{' '}
                {tI('to')} <span>{item.endDate ? dayjs(item.endDate).format(appConfig.formatDate) : ''}</span>{' '}
            </p>
        </div>
    );
};

type IPackageUsageStatisticsCard = {
    dataTotal: number;
    dataUsage: number;
    smsTotal: number;
    smsUsage: number;
    voiceTotal: number;
    voiceUsage: number;
    children?: ReactNode;
};

export const PackageUsageStatisticsCard: FC<IPackageUsageStatisticsCard> = ({
    dataTotal,
    dataUsage,
    smsTotal,
    smsUsage,
    voiceTotal,
    voiceUsage,
    children,
}: IPackageUsageStatisticsCard) => {
    const dataUsagePercent = dataTotal > 0 ? (dataUsage / dataTotal) * 100 : 100;
    const voiceUsagePercent = voiceTotal > 0 ? (voiceUsage / voiceTotal) * 100 : 100;
    const smsUsagePercent = smsTotal > 0 ? (smsUsage / smsTotal) * 100 : 100;
    return (
        <Catcher>
            {children ? children : null}
            <div className={Styles.usageSection}>
                <div className={Styles.usageHeader}>
                    <div className={Styles.usageLabel}>
                        <DatabaseOutlined /> {tF('Data Usage')}
                    </div>
                    <div className={Styles.usagePercentage}>{dataUsagePercent.toFixed(2)}%</div>
                </div>
                <div className={Styles.usageStats}>
                    <div>
                        {showOriginalUnitByType(`${dataUsage}`, UnitTypeValues.DATA)} {tI('used')}
                    </div>
                    <div>
                        {showOriginalUnitByType(`${dataTotal}`, UnitTypeValues.DATA)} {tI('total')}
                    </div>
                </div>
                <Progress percent={dataUsagePercent} showInfo={false} trailColor={'#1890ff'} strokeColor={'#ff4d4f'} />
            </div>
            <div className={Styles.usageSection}>
                <div className={Styles.usageHeader}>
                    <div className={Styles.usageLabel}>
                        <PhoneOutlined /> {tF('Call Minutes')}
                    </div>
                    <div className={Styles.usagePercentage}>{voiceUsagePercent.toFixed(2)}%</div>
                </div>
                <div className={Styles.usageStats}>
                    <div>
                        {showOriginalUnitByType(`${voiceUsage}`, UnitTypeValues.VOICE)} {tI('used')}
                    </div>
                    <div>
                        {showOriginalUnitByType(`${voiceTotal}`, UnitTypeValues.VOICE)} {tI('total')}
                    </div>
                </div>
                <Progress percent={voiceUsagePercent} showInfo={false} trailColor={'#1890ff'} strokeColor={'#ff4d4f'} />
            </div>
            <div className={Styles.usageSection}>
                <div className={Styles.usageHeader}>
                    <div className={Styles.usageLabel}>
                        <MessageOutlined /> {tF('SMS Messages')}
                    </div>
                    <div className={Styles.usagePercentage}>{smsUsagePercent.toFixed(2)}%</div>
                </div>
                <div className={Styles.usageStats}>
                    <div>
                        {showOriginalUnitByType(`${smsUsage}`, UnitTypeValues.SMS)} {tI('used')}
                    </div>
                    <div>
                        {showOriginalUnitByType(`${smsTotal}`, UnitTypeValues.SMS)} {tI('total')}
                    </div>
                </div>
                <Progress percent={smsUsagePercent} showInfo={false} trailColor={'#1890ff'} strokeColor={'#ff4d4f'} />
            </div>
        </Catcher>
    );
};

const PackageUsageStatistics: FC<IPackageUsageStatisticsProps> = ({ item }: IPackageUsageStatisticsProps) => {
    const { tariffPackage, dataVolumePercentageUsage, smsVolumePercentageUsage, voiceVolumePercentageUsage } = item;
    const { id: tariffPackageId, name, dataVolume = 0, smsVolume = 0, voiceVolume = 0 } = tariffPackage;

    const dataVolumeUsageNumber = dataVolume - dataVolume * (dataVolumePercentageUsage / 100);
    const voiceVolumeUsageNumber = voiceVolume - voiceVolume * (voiceVolumePercentageUsage / 100);
    const smsVolumeUsageNumber = smsVolume - smsVolume * (smsVolumePercentageUsage / 100);

    const isCurrent = !!item.startDate && dayjs(item.startDate).isSame(dayjs(), 'month');

    return (
        <Catcher>
            <div key={item.id} className={Styles.packageCard}>
                <PackageUsageStatisticsCard
                    dataTotal={dataVolume}
                    dataUsage={dataVolumeUsageNumber}
                    smsTotal={smsVolume}
                    smsUsage={smsVolumeUsageNumber}
                    voiceTotal={voiceVolume}
                    voiceUsage={voiceVolumeUsageNumber}
                >
                    <div className={Styles.packageTitle}>
                        <Tooltip
                            title={tF(isCurrent ? 'Current month' : 'Next month') + ' ' + getCurrentTextLabel({ item })}
                        >
                            {isCurrent ? (
                                <CaretRightOutlined className={cx(Styles.statusIcon, Styles.current)} />
                            ) : (
                                <ForwardFilled className={cx(Styles.statusIcon, Styles.next)} />
                            )}
                        </Tooltip>
                        <h3>
                            <LinkComponent
                                currentListComponent={'TariffsPackagesList'}
                                id={tariffPackageId}
                                name={name}
                            />
                        </h3>
                    </div>
                </PackageUsageStatisticsCard>
            </div>
        </Catcher>
    );
};

const getListType: IGetApiType = 'GET_TARIFF_PACKAGE_INSTANCE_LIST';

export const PackageUsage = observer(({ cardId }: IPackageUsageProps) => {
    const [page, setPage] = useState(1);
    const [items, setItems] = useState<ITariffPackageInstance[]>([]);
    const [total, setTotal] = useState(0);
    const [isNeedScroll, setIsNeedScroll] = useState<boolean>(false);
    const pageSize = 25;
    const isLoading = useUiIsSpinnerFound([getListType]);
    const startDate = dayjs().startOf('month').format(appConfig.formatDate);
    const fetchData = async (pageNumber: number) => {
        const resp = (await getTariffPackageInstancesList(
            { cardId: cardId, pageNumber, pageSize, boughtForMyCompany: true, startDate },
            true,
        )) as IPackageListPaginated;

        if (resp) {
            const newItems = pageNumber === 1 ? resp.data : [...items, ...resp.data];

            setItems(newItems as ITariffPackageInstance[]);
            setTotal(resp.meta.records_count || items.length);
            if (pageNumber === 1) {
                setIsNeedScroll(true);
            }
        }
    };

    useEffect(() => {
        setPage(1);
        setItems([]);
        fetchData(1);
    }, [cardId]);

    useEffect(() => {
        if (isNeedScroll && !isLoading) {
            setTimeout(() => {
                setIsNeedScroll(false);
                scrollMainContent();
            });
        }
    }, [isNeedScroll, isLoading]);

    const sortItems: ITariffPackageInstance[] = items.sort(
        (a, b) =>
            Number(dayjs(a.startDate)) - Number(dayjs(b.startDate)) ||
            (a.tariffPackage?.name || '').localeCompare(b.tariffPackage?.name || ''),
    );

    let label = '';

    const uniqueDate = sortItems.reduce<string[]>((acc, item) => {
        if (item.startDate !== undefined && !acc.includes(item.startDate)) {
            acc.push(item.startDate);
        }
        return acc;
    }, []);

    const isLongList = sortItems.length > 3 || uniqueDate.length > 1;

    return (
        <Catcher>
            <CardLayout columnOption={cardLayoutOptions} title={'Packages'} isTable idSpinners={[getListType]}>
                <div
                    className={cx(
                        Styles.packageUsageContainer,
                        isLongList ? Styles.packageUsageContainerLong : Styles.packageUsageContainerShort,
                    )}
                >
                    {sortItems.length > 0 ? (
                        <InfiniteScroll
                            pageSize={pageSize}
                            page={page}
                            fetchFunc={fetchData}
                            total={total}
                            current={sortItems.length}
                            handleSetPage={setPage}
                        >
                            {sortItems.map((item: ITariffPackageInstance) => {
                                const currentLabel = getCurrentTextLabel({ item });
                                if (label !== currentLabel) {
                                    label = currentLabel;
                                    return (
                                        <>
                                            <ListLabel key={label} item={item} />
                                            <PackageUsageStatistics key={item.id} item={item} />
                                        </>
                                    );
                                } else {
                                    return <PackageUsageStatistics key={item.id} item={item} />;
                                }
                            })}
                            <div className={Styles.label} />
                        </InfiniteScroll>
                    ) : (
                        <div className={Styles.emptyState}>
                            <InfoCircleOutlined /> {tNM('No packages found')}
                        </div>
                    )}
                </div>
            </CardLayout>
        </Catcher>
    );
});
